import React, { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { useGetWorkshopQuery } from '@api/queries/workshop';
import { useActiveWorkshopGoods } from '@common/hooks/active-workshop-goods';
import { useCheckRequirements } from '@common/hooks/check-requirements';
import { useCountDownBoostsTimers } from '@common/hooks/count-down-boosts-timers';
import { useGoodsProduction } from '@common/hooks/goods-production';
import { useMaxProduction } from '@common/hooks/max-production';
import { calcToProduceValue } from '@common/lib/calcToProduceValue';
import { getImage } from '@common/lib/images';
import { getText } from '@common/lib/text';
import { type FactoryTypeText } from '@common/types/factory';
import { type GoodsType } from '@common/types/goods';
import { ModalContext } from '@components/Modals/Modal/config';
import { PageBackground } from '@components/ui/Backgrounds/PageBackground/PageBackground';
import { WorkshopGoods } from '@components/Workshop/WorkshopGoods/WorkshopGoods';
import { WorkshopNavigation } from '@components/Workshop/WorkshopNavigation/WorkshopNavigation';
import { WorkshopProduction } from '@components/Workshop/WorkshopProduction/WorkshopProduction';
import {
  boostWorkshopTimersEnd,
  selectTargetGoodsByWorkshopId,
  selectWorkshopByNameOrId,
} from '@redux/selectors/workshopSelectors';
import { useAppSelector } from '@redux/store';

import { WorkshopTitle } from '@/components/Workshop/WorkshopTitle/WorkshopTitle';

import { WorkshopContext } from './context';

import css from './styles.module.scss';

const WorkshopPage: React.FC = () => {
  useGetWorkshopQuery(undefined, { refetchOnMountOrArgChange: true });
  const { state } = useLocation() as {
    state?: { goodsName: GoodsType; value: number };
  };
  const { openModal } = useContext(ModalContext);
  const { id } = useParams();
  const workshop = useAppSelector((state) =>
    selectWorkshopByNameOrId(state, id),
  );
  const { t } = useTranslation();
  const targetGoods = useAppSelector((state) =>
    selectTargetGoodsByWorkshopId(state, id),
  );
  const { activeGoods, activeGoodsName, setActiveGoodsName } =
    useActiveWorkshopGoods(workshop, state?.goodsName);
  const max = useMaxProduction(activeGoods?.recipeForProduction);

  const { isComplete } = useCheckRequirements(workshop?.nextLevelRequirements);
  const { producted } = useGoodsProduction({
    incommingStorage: workshop?.incommingStorage,
    localStock: workshop?.localStock,
    rate: targetGoods?.productionRateOriginal,
    plantId: workshop?.id,
  });
  const isTimersEnd = useAppSelector(boostWorkshopTimersEnd);
  useCountDownBoostsTimers(!isTimersEnd, 'Workshop');
  const [toAdd, setToAdd] = useState<number>(
    calcToProduceValue({
      percentage: 25,
      limit: activeGoods?.storageCapacity,
      incommingStorage: workshop?.incommingStorage || 0,
      localStock: workshop?.localStock || 0,
      max,
    }),
  );

  // useEffect(() => {
  //   if (workshop && activeGoods)
  //     dispatch(
  //       addProductedForGood({
  //         id,
  //         producted: workshop.localStock + producted,
  //       }),
  //     );
  // }, [producted, workshop?.localStock, activeGoods]);

  const activeIsTarget = useMemo(
    () =>
      workshop?.targetProduct
        ? activeGoodsName === workshop?.targetProduct
        : true,
    [activeGoodsName, workshop?.targetProduct],
  );

  useEffect(() => {
    if (activeGoods && workshop?.incommingStorage !== undefined) {
      const toAdd = calcToProduceValue({
        percentage: 100,
        limit: activeGoods?.storageCapacity,
        incommingStorage: workshop.incommingStorage,
        localStock: workshop?.localStock || 0,
        max,
      });
      setToAdd(toAdd);
    }
  }, [
    id,
    activeGoods?.storageCapacity,
    workshop?.incommingStorage,
    workshop?.localStock,
    max,
  ]);

  if (!workshop) {
    return null;
  }

  const bg = getImage({ name: workshop.name, field: 'background' });
  const title = getText({ name: workshop.name, kind: 'build', t });

  const openBoostFactory = (): void => {
    openModal({
      renderKind: 'boostFactory',
      props: {
        plantId: workshop.id,
        plant: workshop.name as FactoryTypeText,
        item: activeGoods?.name as GoodsType,
        plantType: 'Workshop',
        level: workshop.level,
      },
    });
  };

  return (
    <WorkshopContext.Provider
      value={{
        toAdd,
        producted,
        incommingStorage: workshop.incommingStorage,
        limit: activeGoods?.storageCapacity,
        activeGoodsName,
        activeGoods,
        setActiveGoodsName,
        targetProduct: workshop.targetProduct,
      }}
    >
      <main className="w-full h-full overflow-y-auto overflow-x-hidden relative">
        <Helmet>
          <title>Workshop</title>
        </Helmet>
        {document.getElementById('toolbar-item-2') &&
          createPortal(
            <WorkshopNavigation />,
            document.getElementById('toolbar-item-2')!,
          )}
        <PageBackground
          variant="page"
          backgroundImage={{ png: bg?.img || '', webp: bg?.webp || '' }}
          className="absolute top-0 left-0 z-[-1] dark-gradient h-[58.64vh]"
        />
        <div className={css.content}>
          <WorkshopTitle>{title}</WorkshopTitle>
          <WorkshopGoods
            workshopId={workshop.id}
            toBeProduced={
              activeIsTarget ? workshop.incommingStorage - producted : 0
            }
            activeGoodsName={activeGoodsName}
            clearDisabled={
              workshop.incommingStorage === 0 ||
              producted === workshop.incommingStorage
            }
          />
          <WorkshopProduction
            id={workshop.id}
            isComplete={isComplete}
            openBoostFactory={openBoostFactory}
            incommingStorage={workshop.incommingStorage}
            producted={producted}
            toAdd={toAdd}
            max={max}
            setToAdd={setToAdd}
            limit={activeGoods?.storageCapacity}
            localStock={workshop.localStock}
            activeIsTarget={activeIsTarget}
          />
        </div>
      </main>
    </WorkshopContext.Provider>
  );
};

export default WorkshopPage;
